import clsx from "clsx";
import type { InputHTMLAttributes } from "react";
import { forwardRef, useId, useState } from "react";
import type { IconType } from "react-icons";
import { FiEye, FiEyeOff } from "react-icons/fi";
import type { ComponentSize } from "@/constants/components/common.constants";
import { INPUT_SIZE_STYLE } from "@/constants/components/input-text.constants";

type InputState = "default" | "success" | "error" | "warning" | "info";

interface InputTextProps extends InputHTMLAttributes<HTMLInputElement> {
  helperText?: string;
  state?: InputState;
  inputSize?: ComponentSize;
  containerClassName?: string;
  leftIcon?: IconType;
  rightIcon?: IconType;
}

const InputText = forwardRef<HTMLInputElement, InputTextProps>(
  (
    {
      id,
      disabled,
      className,
      required,
      helperText,
      containerClassName,
      type = "text",
      inputSize = "md",
      state = "default",
      leftIcon: LeftIcon,
      rightIcon: RightIcon,
      ...props
    },
    ref,
  ) => {
    const generatedId = useId();
    const inputId = id ?? generatedId;
    const helperId = `${inputId}-helper`;
    const errorId = `${inputId}-error`;

    const [showPassword, setShowPassword] = useState(false);
    const isPassword = type === "password";
    const finalType = isPassword ? (showPassword ? "text" : "password") : type;

    const hasError = state === "error";

    return (
      <div className={clsx("w-full space-y-1", containerClassName)}>
        {/* Input Wrapper */}
        <div
          className={clsx(
            "relative flex items-center rounded-md border transition-all",
            {
              "border-gray-300": state === "default",
              "border-green-500": state === "success",
              "border-red-500": hasError,
              "border-yellow-500": state === "warning",
              "border-blue-500": state === "info",
              "bg-gray-100 cursor-not-allowed": disabled,
            },
          )}
        >
          {/* Left Icon */}
          {LeftIcon && (
            <LeftIcon
              aria-hidden
              className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
            />
          )}

          {/* Input */}
          <input
            ref={ref}
            id={inputId}
            type={finalType}
            disabled={disabled}
            aria-invalid={hasError}
            aria-required={required}
            aria-describedby={clsx(helperText && helperId, errorId)}
            className={clsx(
              "w-full bg-transparent rounded-md outline-none text-sm",
              "text-gray-900 dark:text-white focus:shadow-[0_0_0_2px]",
              INPUT_SIZE_STYLE[inputSize].input,
              LeftIcon && "!ps-10",
              (RightIcon || isPassword) && "!pr-10",
              disabled && "cursor-not-allowed",
              className,
              {
                "focus:shadow-primary-500": state === "default",
                "focus:shadow-green-500": state === "success",
                "focus:shadow-red-500": hasError,
                "focus:shadow-yellow-500": state === "warning",
                "focus:shadow-blue-500": state === "info",
              },
            )}
            {...props}
          />

          {/* Password Toggle */}
          {isPassword && (
            <button
              type="button"
              tabIndex={-1}
              onClick={() => !hasError && setShowPassword((v) => !v)}
              className="absolute right-2 text-gray-400 hover:text-gray-600"
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? <FiEyeOff /> : <FiEye />}
            </button>
          )}

          {/* Right Icon */}
          {!isPassword && RightIcon && (
            <span className="absolute right-3 text-gray-400">
              <RightIcon aria-hidden />
            </span>
          )}
        </div>

        {/* Helper / Error */}
        {helperText && (
          <p id={helperId} className="text-xs text-gray-500 dark:text-gray-400">
            {helperText}
          </p>
        )}
      </div>
    );
  },
);

InputText.displayName = "InputText";

export default InputText;
